import { useSlackUsersList } from "@/components/EntitySelector/integrations/SlackSelector";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import {
  Contact,
  ContactEntity,
  SlackUserEntity,
} from "@cohesive/api-entities";
import { getEntityId, Spinner } from "@cohesive/ui-components";
import { ControlProps, optionIs, rankWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { Check, ChevronsUpDown, User, X } from "lucide-react";
import React, { useMemo, useState } from "react";

type SingleData = SlackUserEntity | string | undefined;
type MultiData = (SlackUserEntity | string)[] | undefined;

interface SlackUserControlProps extends ControlProps {
  data: SingleData | MultiData;
}

const SlackUserControlComponent: React.FC<SlackUserControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  label,
  schema,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const integration = useIntegrationConnection("slack");

  // Determine if multi-select mode based on schema type or data being an array
  const isMultiSelect = schema?.type === "array" || Array.isArray(data);

  // Extract selected IDs (handles both single and multi-select)
  const selectedIds = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) {
      return data
        .map((item) =>
          typeof item === "string" ? item : getEntityId(item as any),
        )
        .filter(Boolean) as string[];
    }
    const id = typeof data === "string" ? data : getEntityId(data as any);
    return id ? [id] : [];
  }, [data]);

  const { data: usersData, isLoading } = useSlackUsersList({
    connection: integration!,
  });

  const users = usersData || [];

  const renderContactIcon = (contact: Contact) => {
    if (contact.image_url) {
      return (
        <img
          src={contact.image_url}
          alt={contact.name}
          className="w-7 h-7 shrink-0 rounded-full"
        />
      );
    }

    return (
      <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
        <User className="w-4! h-4!" />
      </div>
    );
  };

  // Find selected users from the list
  const selectedUsers = useMemo(
    () => users.filter((user) => selectedIds.includes(user.data.id)),
    [users, selectedIds],
  );

  const isSelected = (userId: string) => selectedIds.includes(userId);

  const handleSelect = (user: ContactEntity) => {
    if (isMultiSelect) {
      const currentData = Array.isArray(data) ? data : [];
      if (isSelected(user.data.id)) {
        // Remove user from selection
        const newData = currentData.filter((item) => {
          const id = typeof item === "string" ? item : getEntityId(item as any);
          return id !== user.data.id;
        });
        handleChange(path, newData.length > 0 ? newData : undefined);
      } else {
        // Add user to selection
        handleChange(path, [...currentData, user]);
      }
    } else {
      // Single select mode
      handleChange(path, user);
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleChange(path, isMultiSelect ? [] : null);
  };

  const renderSelectedValue = () => {
    if (selectedUsers.length === 0) {
      return (
        <span className="text-muted-foreground">
          {isMultiSelect ? "Select Slack users..." : "Select Slack user..."}
        </span>
      );
    }

    const firstUser = selectedUsers[0];
    return (
      <div className="flex items-center gap-2 overflow-hidden">
        {renderContactIcon(firstUser.data)}
        <span className="truncate">
          {firstUser.data.name}
          {selectedUsers.length > 1 && (
            <span className="text-muted-foreground ml-1">
              +{selectedUsers.length - 1} more
            </span>
          )}
        </span>
      </div>
    );
  };

  return (
    <>
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={!enabled}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {renderSelectedValue()}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedUsers.length > 0 && (
                <div
                  role="button"
                  aria-label="Clear selection"
                  className="cursor-pointer"
                  onClick={handleClear}
                >
                  <X className="h-4 w-4 opacity-50 hover:opacity-100" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search users..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Spinner size="sm" />
                </div>
              ) : (
                <>
                  <CommandEmpty>No users found.</CommandEmpty>
                  <CommandGroup>
                    {users
                      .filter((user: ContactEntity) => {
                        const { name = "", email = "" } = user.data;
                        return (
                          name
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          (email || "")
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase())
                        );
                      })
                      .map((user: ContactEntity) => (
                        <CommandItem
                          key={user.data.id}
                          value={user.data.id}
                          onSelect={() => handleSelect(user)}
                        >
                          <div className="flex items-center gap-2 w-full">
                            {renderContactIcon(user.data)}
                            <div className="flex flex-col flex-1">
                              <span>{user.data.name}</span>
                              {user.data.email && (
                                <span className="text-xs text-muted-foreground">
                                  {user.data.email}
                                </span>
                              )}
                            </div>
                            {isSelected(user.data.id) && (
                              <Check className="h-4 w-4 text-primary" />
                            )}
                          </div>
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
};

export const slackUserTester = rankWith(5, optionIs("control", "slack_user"));
export const SlackUserControl = withJsonFormsControlProps(
  SlackUserControlComponent,
);
